// services/questionTypeService.ts
import { QuestionTypeMaster } from '../types';
import { db } from '../firebase/config';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';

const COLLECTION = 'questionTypeMaster';

type QuestionTypeDB = Omit<QuestionTypeMaster, 'id'>;

const convertDoc = (doc: QueryDocumentSnapshot<DocumentData>): QuestionTypeMaster => {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name,
    code: data.code,
    answerFormat: data.answerFormat || 'single_select',
    evaluationType: data.evaluationType || 'auto',
    scoringMethod: data.scoringMethod || 'no_right_wrong',
    uiType: data.uiType || 'radio',
    usesScaleMaster: data.usesScaleMaster ?? false,
    usesTraitMapping: data.usesTraitMapping ?? false,
    allowsReverseScoring: data.allowsReverseScoring ?? false,
    requiresOptions: data.requiresOptions ?? false,
    requiresCorrectAnswer: data.requiresCorrectAnswer ?? false,
    negativeMarking: data.negativeMarking ?? false,
    requiresMultipleCorrectAnswers: data.requiresMultipleCorrectAnswers ?? false,
    partialScoringAllowed: data.partialScoringAllowed ?? false,
    requiresLeftRightItems: data.requiresLeftRightItems ?? false,
    requiresCorrectMapping: data.requiresCorrectMapping ?? false,
    requiresItemList: data.requiresItemList ?? false,
    requiresCorrectOrder: data.requiresCorrectOrder ?? false,
    requiresCategories: data.requiresCategories ?? false,
    requiresItems: data.requiresItems ?? false,
    requiresCorrectGrouping: data.requiresCorrectGrouping ?? false,
    description: data.description || '',
    isActive: data.isActive ?? true,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt
  };
};

export const getQuestionTypes = async (onlyActive: boolean = true): Promise<QuestionTypeMaster[]> => {
  try {
    const collectionRef = collection(db, COLLECTION);
    const constraints = [];
    
    if (onlyActive) {
      constraints.push(where('isActive', '==', true));
    }
    constraints.push(orderBy('name', 'asc'));
    
    const q = query(collectionRef, ...constraints);
    const snapshot = await getDocs(q);

    const types: QuestionTypeMaster[] = [];
    snapshot.forEach((doc) => {
      types.push(convertDoc(doc));
    });

    return types;
  } catch (error) {
    console.error('Error fetching question types:', error);
    return [];
  }
};

export const getQuestionTypeByCode = async (code: string): Promise<QuestionTypeMaster | null> => {
  try {
    const collectionRef = collection(db, COLLECTION);
    const q = query(collectionRef, where('code', '==', code));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return convertDoc(snapshot.docs[0]);
    }
    return null;
  } catch (error) {
    console.error('Error fetching question type:', error);
    return null;
  }
};

export const getQuestionTypesBySectionType = async (sectionType: string): Promise<QuestionTypeMaster[]> => {
  try {
    const allTypes = await getQuestionTypes(true);
    
    const mapping: Record<string, string[]> = {
      'psychometric': ['single_select_scale', 'likert_scale', 'psychometric_scale'],
      'aptitude': ['mcq_single', 'mcq_multiple', 'true_false', 'numerical', 'fill_blanks', 'matching', 'ranking'],
      'hybrid': ['single_select_scale', 'likert_scale', 'psychometric_scale', 'mcq_single', 'mcq_multiple', 'true_false', 'numerical', 'fill_blanks', 'matching', 'ranking'],
      'mixed': ['single_select_scale', 'likert_scale', 'psychometric_scale', 'mcq_single', 'mcq_multiple', 'true_false', 'numerical', 'fill_blanks', 'matching', 'ranking']
    };
    
    let allowedCodes = mapping[sectionType] || mapping['aptitude'];
    const availableCodes = allTypes.map(t => t.code);
    allowedCodes = allowedCodes.filter(code => availableCodes.includes(code));
    const filteredTypes = allTypes.filter(t => allowedCodes.includes(t.code));
    
    console.log(`Question types for section type "${sectionType}":`, filteredTypes);
    
    return filteredTypes;
  } catch (error) {
    console.error('Error getting question types by section type:', error);
    return [];
  }
};

export const createQuestionType = async (data: any): Promise<QuestionTypeMaster> => {
  try {
    const now = new Date().toISOString();
    const payload = {
      name: data.name,
      code: data.code.toLowerCase().replace(/\s/g, '_'),
      answerFormat: data.answerFormat,
      evaluationType: data.evaluationType,
      scoringMethod: data.scoringMethod,
      uiType: data.uiType,
      usesScaleMaster: data.usesScaleMaster,
      usesTraitMapping: data.usesTraitMapping,
      allowsReverseScoring: data.allowsReverseScoring,
      requiresOptions: data.requiresOptions,
      requiresCorrectAnswer: data.requiresCorrectAnswer,
      negativeMarking: data.negativeMarking,
      requiresMultipleCorrectAnswers: data.requiresMultipleCorrectAnswers,
      partialScoringAllowed: data.partialScoringAllowed,
      requiresLeftRightItems: data.requiresLeftRightItems,
      requiresCorrectMapping: data.requiresCorrectMapping,
      requiresItemList: data.requiresItemList,
      requiresCorrectOrder: data.requiresCorrectOrder,
      requiresCategories: data.requiresCategories,
      requiresItems: data.requiresItems,
      requiresCorrectGrouping: data.requiresCorrectGrouping,
      description: data.description,
      isActive: data.isActive,
      createdAt: now,
      updatedAt: now
    };

    const docRef = await addDoc(collection(db, COLLECTION), payload);
    return { id: docRef.id, ...payload } as QuestionTypeMaster;
  } catch (error) {
    console.error('Error creating question type:', error);
    throw error;
  }
};

export const updateQuestionType = async (id: string, data: any): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    const updateData: any = {
      ...data,
      updatedAt: new Date().toISOString()
    };
    
    if (data.code) {
      updateData.code = data.code.toLowerCase().replace(/\s/g, '_');
    }
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating question type:', error);
    throw error;
  }
};

export const deleteQuestionType = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting question type:', error);
    throw error;
  }
};
